import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, FileQuestion, Upload, KeyRound, Link2, Copy, Trash2, Play, Users, ClipboardList, FileSpreadsheet, Download } from 'lucide-react';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export default function CompetencyTestsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = true; // backend enforces (admin/manager/supervisor)
  const [tests, setTests] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [dlg, setDlg] = useState(null);        // create/edit
  const [manage, setManage] = useState(null);  // manage dialog data {test, questions, candidates, results, answerKey}
  const [qEdit, setQEdit] = useState([]);       // editable questions (creator)
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [candDlg, setCandDlg] = useState(false);   // add-candidate dialog
  const [candEmpId, setCandEmpId] = useState('');
  const [candAdding, setCandAdding] = useState(false);
  const [backfillDlg, setBackfillDlg] = useState(false);
  const [backfillRows, setBackfillRows] = useState([]);  // [{...row, pick}]
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillApplying, setBackfillApplying] = useState(false);

  const load = async () => {
    try {
      const [t, tr] = await Promise.all([hrAPI.listTests(), hrAPI.listTrainings()]);
      setTests(t.data || []); setTrainings(tr.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    hrAPI.listEmployees()
      .then(res => setEmployees((res.data || []).filter(e => (e.status || 'active') !== 'inactive')))
      .catch(() => {});
  }, []);

  const openNew = () => setDlg({ name: '', given_date: '', training_id: '', time_limit_min: 30, num_questions: 0 });

  const saveTest = async () => {
    if (!dlg.name.trim()) { toast.error('Test name required'); return; }
    if (!dlg.training_id) { toast.error('Select the linked training'); return; }
    setSaving(true);
    try {
      const payload = { ...dlg, time_limit_min: Number(dlg.time_limit_min) || 30, num_questions: Number(dlg.num_questions) || 0, questions: [] };
      const r = await hrAPI.createTest(payload);
      toast.success('Test created — now import or add questions'); setDlg(null); await load();
      openManage(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const openManage = async (id) => {
    try {
      const t = (await hrAPI.getTest(id)).data;
      const [cands, results] = await Promise.all([hrAPI.listCandidates(id).catch(() => ({ data: [] })), hrAPI.testResults(id).catch(() => ({ data: [] }))]);
      let ak = null;
      if (t.is_creator) { try { ak = (await hrAPI.getAnswerKey(id)).data; } catch { /* */ } }
      setManage({ test: t, candidates: cands.data || [], results: results.data || [], answerKey: ak });
      setQEdit(JSON.parse(JSON.stringify(t.questions || [])));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to open'); }
  };

  const refreshManage = () => manage && openManage(manage.test.id);

  const downloadTemplate = async () => {
    try {
      const res = await hrAPI.downloadTestTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'competency_test_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  const uploadExcel = () => {
    const inp = document.createElement('input'); inp.type = 'file'; inp.accept = '.xlsx,.xls';
    inp.onchange = async () => {
      const f = inp.files[0]; if (!f) return;
      const fd = new FormData(); fd.append('file', f);
      try { const r = await hrAPI.importTestExcel(manage.test.id, fd); toast.success(`Imported ${r.data.imported} questions (${r.data.with_answer_key} with answers)`); refreshManage(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    };
    inp.click();
  };

  const upload = async (kind) => {
    const inp = document.createElement('input'); inp.type = 'file'; inp.accept = '.pdf';
    inp.onchange = async () => {
      const f = inp.files[0]; if (!f) return;
      const fd = new FormData(); fd.append('file', f);
      try {
        if (kind === 'q') { const r = await hrAPI.importTestQuestions(manage.test.id, fd); toast.success(`Imported ${r.data.imported} questions`); }
        else { const r = await hrAPI.importTestAnswerKey(manage.test.id, fd); toast.success(`Answer key applied to ${r.data.applied} questions`); }
        refreshManage();
      } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    };
    inp.click();
  };

  const openAddCandidate = () => { setCandEmpId(''); setCandDlg(true); };  const submitAddCandidate = async () => {
    if (!candEmpId) { toast.error('Select an employee'); return; }
    const emp = employees.find(e => e.id === candEmpId);
    setCandAdding(true);
    try {
      const r = await hrAPI.addCandidate(manage.test.id, {
        name: emp?.name || '', email: emp?.email || '', employee_id: candEmpId, base_url: window.location.origin,
      });
      toast.success(r.data.emailed ? 'Candidate added & link emailed' : 'Candidate added');
      setCandDlg(false); refreshManage();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCandAdding(false); }
  };

  const openBackfill = async () => {
    setBackfillDlg(true); setBackfillLoading(true); setBackfillRows([]);
    try {
      const r = await hrAPI.backfillUnlinkedCandidates();
      setBackfillRows((r.data.unlinked || []).map(u => ({ ...u, pick: u.suggested_employee_id || '' })));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setBackfillLoading(false); }
  };
  const applyBackfill = async () => {
    const mappings = backfillRows.filter(r => r.pick).map(r => ({ attempt_id: r.attempt_id, employee_id: r.pick }));
    if (mappings.length === 0) { toast.error('Pick an employee for at least one candidate'); return; }
    setBackfillApplying(true);
    try {
      const r = await hrAPI.backfillApplyCandidates({ mappings });
      toast.success(`Linked ${r.data.linked} candidate(s); ${r.data.synced} score(s) pushed to skill updates`);
      setBackfillDlg(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBackfillApplying(false); }
  };

  // ---- manual question editor ----
  const blankQ = () => ({ id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text: '', options: ['', '', '', ''], correct_index: null, section: '' });
  const addQuestion = () => setQEdit(qs => [...qs, blankQ()]);
  const updQ = (i, patch) => setQEdit(qs => qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const updOpt = (i, oi, val) => setQEdit(qs => qs.map((q, idx) => idx === i ? { ...q, options: q.options.map((o, k) => k === oi ? val : o) } : q));
  const delQ = (i) => setQEdit(qs => qs.filter((_, idx) => idx !== i));
  const saveQuestions = async () => {
    for (const q of qEdit) {
      if (!q.text.trim()) { toast.error('Every question needs text'); return; }
      if (q.options.filter(o => o.trim()).length < 2) { toast.error('Each question needs at least 2 options'); return; }
      if (q.correct_index == null) { toast.error(`Mark the correct answer for: "${q.text.slice(0, 30)}..."`); return; }
    }
    setSaving(true);
    try {
      const t = manage.test;
      await hrAPI.updateTest(t.id, { name: t.name, given_date: t.given_date, training_id: t.training_id, time_limit_min: t.time_limit_min, num_questions: t.num_questions, questions: qEdit.map(q => ({ ...q, id: q.id.startsWith('new-') ? undefined : q.id })) });
      toast.success('Questions saved'); refreshManage();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const delCandidate = async (attId) => { if (!window.confirm('Remove candidate & their link?')) return; try { await hrAPI.deleteCandidate(manage.test.id, attId); refreshManage(); } catch (e) { toast.error('Failed'); } };
  const copyLink = (path) => { const url = `${window.location.origin}${path}`; navigator.clipboard?.writeText(url); toast.success('Link copied'); };
  const delTest = async (id) => { if (!window.confirm('Delete this test and all attempts?')) return; try { await hrAPI.deleteTest(id); toast.success('Deleted'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };

  return (
    <div className="space-y-5" data-testid="hr-tests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><FileQuestion className="w-6 h-6 text-pink-600" /> Competency Tests</h1>
          <p className="text-sm text-slate-500 mt-0.5">Create tests, import questions/answer key from PDF, link to a training, and share links or let attendees take it after completion.</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && <Button size="sm" variant="outline" className="gap-1" onClick={openBackfill} data-testid="link-past-candidates"><Link2 className="w-4 h-4" /> Link Past Candidates</Button>}
          {canCreate && <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={openNew} data-testid="test-add"><Plus className="w-4 h-4" /> New Test</Button>}
        </div>
      </div>

      <div className="space-y-3">
        {tests.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No tests yet</p>}
        {tests.map(t => (
          <Card key={t.id} className="border border-slate-200 shadow-none" data-testid={`test-card-${t.id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-[#0F172A]">{t.name} {t.is_creator && <Badge variant="outline" className="text-[9px] ml-1 bg-pink-50 text-pink-700 border-pink-200">Creator</Badge>}</p>
                <p className="text-xs text-slate-500 mt-0.5">Training: {t.training_title || '—'} · Given: {t.given_date || '—'} · {t.question_count} Qs · {t.num_questions || 'all'} per attempt · {t.time_limit_min} min · by {t.created_by_name}</p>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => navigate(`/hr/tests/${t.id}/take`)} data-testid={`test-take-${t.id}`}><Play className="w-3.5 h-3.5" /> Take</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => openManage(t.id)} data-testid={`test-manage-${t.id}`}><ClipboardList className="w-3.5 h-3.5" /> Manage</Button>
                {(t.is_creator || user?.role === 'admin' || user?.app_roles?.hr_training === 'manager') && <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" onClick={() => delTest(t.id)} data-testid={`test-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md" data-testid="test-create-dialog">
          <DialogHeader><DialogTitle>New Competency Test</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Test name *</Label><Input value={dlg.name} onChange={e => setDlg({ ...dlg, name: e.target.value })} data-testid="test-name" /></div>
              <div><Label className="text-xs">Test given date</Label><Input type="date" value={dlg.given_date} onChange={e => setDlg({ ...dlg, given_date: e.target.value })} data-testid="test-date" /></div>
              <div><Label className="text-xs">Linked training *</Label>
                <Select value={dlg.training_id || undefined} onValueChange={v => setDlg({ ...dlg, training_id: v })}>
                  <SelectTrigger data-testid="test-training"><SelectValue placeholder="Select training" /></SelectTrigger>
                  <SelectContent>{trainings.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Time limit (min)</Label><Input type="number" value={dlg.time_limit_min} onChange={e => setDlg({ ...dlg, time_limit_min: e.target.value })} data-testid="test-timelimit" /></div>
                <div><Label className="text-xs">Questions per attempt (0=all)</Label><Input type="number" value={dlg.num_questions} onChange={e => setDlg({ ...dlg, num_questions: e.target.value })} data-testid="test-numq" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button><Button onClick={saveTest} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="test-save">{saving ? 'Creating...' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage dialog */}
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="test-manage-dialog">
          <DialogHeader><DialogTitle>{manage?.test?.name}</DialogTitle></DialogHeader>
          {manage && (
            <Tabs defaultValue="questions">
              <TabsList>
                <TabsTrigger value="questions" data-testid="tab-questions">Questions ({manage.test.questions?.length || 0})</TabsTrigger>
                <TabsTrigger value="candidates" data-testid="tab-candidates">Links ({manage.candidates.length})</TabsTrigger>
                <TabsTrigger value="results" data-testid="tab-results">Results ({manage.results.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="questions" className="space-y-3">
                {manage.test.is_creator ? (
                  <>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => upload('q')} data-testid="import-questions"><Upload className="w-4 h-4" /> Import questions (PDF)</Button>
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => upload('a')} data-testid="import-answerkey"><KeyRound className="w-4 h-4" /> Import answer key (PDF)</Button>
                      <Button size="sm" variant="outline" className="gap-1" onClick={uploadExcel} data-testid="import-excel"><FileSpreadsheet className="w-4 h-4" /> Import Excel (Q + key)</Button>
                      <Button size="sm" variant="ghost" className="gap-1 text-slate-500" onClick={downloadTemplate} data-testid="download-template"><Download className="w-4 h-4" /> Template</Button>
                      <Button size="sm" variant="outline" className="gap-1" onClick={addQuestion} data-testid="add-question"><Plus className="w-4 h-4" /> Add question</Button>
                      <Button size="sm" className="gap-1 bg-pink-600 hover:bg-pink-700 text-white ml-auto" onClick={saveQuestions} disabled={saving} data-testid="save-questions">{saving ? 'Saving...' : 'Save questions'}</Button>
                    </div>
                    <div className="space-y-3">
                      {qEdit.map((q, i) => (
                        <div key={q.id} className="border border-slate-200 rounded p-3 space-y-2" data-testid={`qedit-${i}`}>
                          <div className="flex items-start gap-2">
                            <span className="text-sm font-semibold text-slate-500 mt-1.5">{i + 1}.</span>
                            <Input value={q.text} onChange={e => updQ(i, { text: e.target.value })} placeholder="Question text" className="text-sm" data-testid={`qedit-text-${i}`} />
                            <Button size="sm" variant="ghost" className="text-red-600 mt-0.5" onClick={() => delQ(i)} data-testid={`qedit-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pl-6">
                            {q.options.map((o, oi) => (
                              <label key={oi} className="flex items-center gap-2">
                                <input type="radio" name={`correct-${q.id}`} checked={q.correct_index === oi} onChange={() => updQ(i, { correct_index: oi })} title="Mark correct" data-testid={`qedit-correct-${i}-${oi}`} />
                                <span className="text-xs font-semibold text-slate-400">{LETTERS[oi]}</span>
                                <Input value={o} onChange={e => updOpt(i, oi, e.target.value)} placeholder={`Option ${LETTERS[oi]}`} className="text-sm h-8" data-testid={`qedit-opt-${i}-${oi}`} />
                              </label>
                            ))}
                          </div>
                          <div className="pl-6"><Input value={q.section || ''} onChange={e => updQ(i, { section: e.target.value })} placeholder="Section / topic tag (optional, e.g. A - DPD Basics)" className="text-xs h-8 max-w-md" data-testid={`qedit-section-${i}`} /></div>
                        </div>
                      ))}
                      {qEdit.length === 0 && <p className="text-xs text-slate-400">No questions yet — import a PDF or click "Add question".</p>}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-amber-600">Answer key is visible only to the test creator.</p>
                    <div className="space-y-2">
                      {(manage.test.questions || []).map((q, i) => (
                        <div key={q.id} className="text-sm border border-slate-200 rounded p-2" data-testid={`mq-${i}`}>
                          <p className="font-medium">{i + 1}. {q.text} {q.section ? <Badge variant="outline" className="text-[9px] ml-1">{q.section}</Badge> : null}</p>
                          <ul className="mt-1 space-y-0.5">
                            {q.options.map((o, oi) => <li key={oi} className="text-xs text-slate-600">{LETTERS[oi]}. {o}</li>)}
                          </ul>
                        </div>
                      ))}
                      {(manage.test.questions || []).length === 0 && <p className="text-xs text-slate-400">No questions yet.</p>}
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="candidates" className="space-y-3">
                <Button size="sm" variant="outline" className="gap-1" onClick={openAddCandidate} data-testid="add-candidate"><Users className="w-4 h-4" /> Add candidate</Button>
                <Table>
                  <TableHeader><TableRow className="bg-slate-50/80"><TableHead className="text-xs">Name</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs">Score</TableHead><TableHead className="text-xs text-right">Link</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {manage.candidates.map(c => (
                      <TableRow key={c.id} data-testid={`cand-${c.id}`}>
                        <TableCell className="text-sm">{c.taker_name}<div className="text-[10px] text-slate-400">{c.taker_email}</div></TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] ${c.status === 'submitted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{c.status}</Badge></TableCell>
                        <TableCell className="text-sm">{c.score != null ? `${c.score}/${c.total}` : '—'}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => copyLink(c.path)} data-testid={`copy-link-${c.id}`}><Copy className="w-3.5 h-3.5" /> Copy</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => delCandidate(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {manage.candidates.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-xs text-slate-400 py-4">No external candidates yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
                <p className="text-[11px] text-slate-400 flex items-center gap-1"><Link2 className="w-3 h-3" /> Each candidate gets a unique link openable without login.</p>
              </TabsContent>

              <TabsContent value="results">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50/80"><TableHead className="text-xs">Taker</TableHead><TableHead className="text-xs">Type</TableHead><TableHead className="text-xs">Score</TableHead><TableHead className="text-xs">Submitted</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {manage.results.map((r, i) => (
                      <TableRow key={i} data-testid={`result-${i}`}>
                        <TableCell className="text-sm">{r.taker_name}</TableCell>
                        <TableCell className="text-xs">{r.mode === 'link' ? 'External' : 'Employee'}</TableCell>
                        <TableCell className="text-sm font-medium">{r.score}/{r.total}
                          {r.sections && Object.keys(r.sections).length > 1 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(r.sections).map(([s, v]) => <Badge key={s} variant="outline" className="text-[9px] font-normal">{s}: {v.correct}/{v.total}</Badge>)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{(r.submitted_at || '').slice(0, 16).replace('T', ' ')}</TableCell>
                      </TableRow>
                    ))}
                    {manage.results.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-xs text-slate-400 py-4">No submissions yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Add candidate — pick from existing employees */}
      <Dialog open={candDlg} onOpenChange={setCandDlg}>
        <DialogContent className="sm:max-w-md" data-testid="add-candidate-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Add Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Employee *</Label>
            <SearchableSelect
              options={employees.map(e => ({ value: e.id, label: e.emp_code ? `${e.name} (${e.emp_code})` : e.name, sub: e.email, searchStr: `${e.name} ${e.emp_code || ''} ${e.email || ''}` }))}
              value={candEmpId} onSelect={setCandEmpId}
              placeholder="Search employee..." emptyText="No employees found." testId="add-candidate-emp-select"
            />
            <p className="text-[11px] text-slate-500">
              Candidates must be existing employees so their test score flows into Pending Skill Updates.
              A unique test link is generated (and emailed if the employee has an email on file).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCandDlg(false)}>Cancel</Button>
            <Button size="sm" onClick={submitAddCandidate} disabled={candAdding || !candEmpId} data-testid="add-candidate-submit">
              {candAdding ? 'Adding…' : 'Add & Generate Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Past Candidates — backfill free-text attempts to employees */}
      <Dialog open={backfillDlg} onOpenChange={setBackfillDlg}>
        <DialogContent className="sm:max-w-3xl" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Link Past Candidates to Employees</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-slate-500">
            These external (free-text) submissions aren't linked to an employee, so their scores never reached Pending Skill Updates.
            Pick the matching employee for each and apply. Exact name matches are pre-selected.
          </p>
          <div className="max-h-[55vh] overflow-auto border rounded-md mt-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Candidate</TableHead>
                  <TableHead className="text-xs">Test</TableHead>
                  <TableHead className="text-xs">Score</TableHead>
                  <TableHead className="text-xs w-[280px]">Link to Employee</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backfillLoading && <TableRow><TableCell colSpan={4} className="text-center text-xs text-slate-400 py-4">Loading…</TableCell></TableRow>}
                {!backfillLoading && backfillRows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-xs text-slate-400 py-4">No unlinked candidate submissions 🎉</TableCell></TableRow>}
                {backfillRows.map((row, i) => (
                  <TableRow key={row.attempt_id} data-testid={`backfill-row-${i}`}>
                    <TableCell className="text-xs font-medium">
                      {row.taker_name}
                      {row.match === 'exact' && <Badge className="ml-1 text-[9px] bg-emerald-100 text-emerald-700">match</Badge>}
                      {row.match === 'ambiguous' && <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-700">{row.ambiguous_count} matches</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">{row.test_name}</TableCell>
                    <TableCell className="text-xs font-mono">{row.score != null ? `${row.score}/${row.total}` : '—'}</TableCell>
                    <TableCell>
                      <SearchableSelect
                        options={employees.map(e => ({ value: e.id, label: e.emp_code ? `${e.name} (${e.emp_code})` : e.name, searchStr: `${e.name} ${e.emp_code || ''} ${e.email || ''}` }))}
                        value={row.pick}
                        onSelect={v => setBackfillRows(rows => rows.map((r, idx) => idx === i ? { ...r, pick: v } : r))}
                        placeholder="Select employee…" emptyText="No employees." testId={`backfill-pick-${i}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setBackfillDlg(false)}>Cancel</Button>
            <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white" onClick={applyBackfill}
              disabled={backfillApplying || backfillRows.filter(r => r.pick).length === 0} data-testid="backfill-apply">
              {backfillApplying ? 'Applying…' : `Link ${backfillRows.filter(r => r.pick).length} candidate(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
